import { type ApiError, isApiError, useConfig } from '@fuze/portal-core';

import { useGetCurrentUser } from '@/api/generated/fuze-portals-api';
import type { CurrentUser } from '@/api/generated/fuze-portals-api.schemas';

type Account = NonNullable<CurrentUser['account']>;

/**
 * Who is using the portal, from `GET /me`:
 * - `signed-out`: no session (401). `expired` when the API says it timed out, so login can say so.
 * - `needs-account`: signed in, but the portal account is only created when they confirm their NPI.
 * - `signed-in`: signed in with an account; `user.account.role` is prescriber, office_staff or aes.
 */
export type Session =
  | { status: 'loading' }
  | { status: 'signed-out'; expired: boolean }
  | { status: 'needs-account'; user: CurrentUser }
  | { status: 'signed-in'; user: CurrentUser & { account: Account } };

const isUnauthorized = (error: unknown) => isApiError(error) && error.status === 401;

/** Options for every `GET /me` read, so the cached query keeps one meta whoever reads it first. */
const sessionQuery = {
  // A 401 here is the normal signed-out state, not an expiry mid-session: the guards handle it, not the cache.
  meta: { handlesUnauthorized: true },
  // Anything else (5xx, network) is a broken page: let the screen error boundary show it.
  throwOnError: (error: ApiError) => !isUnauthorized(error),
};

/**
 * The session in all its states. Only the route guards read this; screens use `useSignedInUser`, so they never
 * handle states their route can't reach. Not exported from `session/index.ts` for that reason.
 */
export const useSession = (): Session => {
  const { devAccountRole } = useConfig();
  const { data, error, isPending } = useGetCurrentUser({ query: sessionQuery });
  if (error !== null) {
    return { status: 'signed-out', expired: error.code === 'SESSION_EXPIRED' };
  }
  if (isPending) {
    return { status: 'loading' };
  }
  // TODO: drop the fallback with `devAccountRole` (see its schema) once `/me` returns the account.
  const account = data.account ?? (devAccountRole === undefined ? undefined : { role: devAccountRole });
  return account === undefined
    ? { status: 'needs-account', user: data }
    : { status: 'signed-in', user: { ...data, account } };
};

/**
 * The signed-in user, for screens under `RequireAccount`, which only renders them once `/me` returned an account.
 * Reads the same cached `/me` query, so it adds no request.
 */
export const useSignedInUser = () => {
  const session = useSession();
  if (session.status !== 'signed-in') {
    throw new Error('useSignedInUser is only for screens under RequireAccount');
  }
  return session.user;
};
